import { FC, useState, useRef, useEffect } from 'react';
import { Button, DropDownModal } from '@marriott/mi-ui-library';
import { MobileCalendarContainerProps } from './MobileCalendarContainer.types';
import { StyledMobileCalendarContainer } from './MobileCalendarContainer.styles';
import { TextFormField, MobileCalendar } from '../../../molecules';
import { CalendarTabValue } from '../MobileCalendar/MobileCalendar.types';
import { getFormattedDateObject, getFormattedDates, isToday } from '../../../utils';

export const MobileCalendarContainer: FC<MobileCalendarContainerProps> = ({
  dates,
  ctas,
  startDate,
  endDate,
  isDateRangeFixed,
  rangeStartDate,
  rangeEndDate,
  setDefaultDates,
  showErrorMessage,
  errorMessage,
  isPastDateSelectionDisabled,
  enableFromPastDate,
  calendarTab: calendarTabProp,
  onCalendarTabChange,
  onChange,
  onApply,
  onClose,
  hideDatesBadge,
}) => {
  const { reset, resetAriaLabel } = ctas;

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [dateError, setDateError] = useState('');
  const [calendarTab, setCalendarTab] = useState<CalendarTabValue>(calendarTabProp ?? CalendarTabValue.MULTIPLE);
  const [dateValues, setDateValues] = useState(() => {
    if (startDate && endDate) {
      const start = getFormattedDateObject(startDate, 'dateWithDayMonthAndYear');
      const end = getFormattedDateObject(endDate, 'dateWithDayMonthAndYear');
      return { dates: getFormattedDates(start, end), startDate, endDate };
    }
    return { dates: '', startDate: '', endDate: '' };
  });

  const buttonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (calendarTabProp !== undefined) {
      setCalendarTab(calendarTabProp);
    }
  }, [calendarTabProp]);

  useEffect(() => {
    document.body.style.overflow = isModalOpen ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [isModalOpen]);

  const handleCalendarTabChange = (nextTab: CalendarTabValue) => {
    setCalendarTab(nextTab);
    onCalendarTabChange?.(nextTab);
  };

  const handleCalendarChange = (datesStr: string, start: string, end: string) => {
    setDateValues({ dates: datesStr, startDate: start, endDate: end });
    setDateError('');
    onChange?.(datesStr, start, end);
  };

  const handleApply = () => {
    const isDateMissing = !dateValues.startDate || (calendarTab === CalendarTabValue.MULTIPLE && !dateValues.endDate);
    if (isDateMissing) {
      setDateError(dates.requiredError);
      return;
    }
    if (isToday(dateValues.startDate)) {
      setDateError(dates?.currentDateError || '');
      return;
    }
    setDateError('');
    setIsModalOpen(false);
    onApply?.(dateValues.dates, dateValues.startDate, dateValues.endDate);
    onClose?.();
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    onClose?.();
  };

  return (
    <StyledMobileCalendarContainer
      data-testid="groups-MobileCalendarContainer"
      data-component-name="m-groups-MobileCalendarContainer"
    >
      <TextFormField
        label={dates.label}
        ariaLabel={dates.ariaLabel}
        className="m-input-field"
        value={dateValues.dates}
        showErrorMessage={showErrorMessage || !!dateError}
        errorMessage={dateError || errorMessage}
        getInputProps={() => ({
          readOnly: true,
        })}
        onClick={() => setIsModalOpen(true)}
      />
      <DropDownModal
        className="mobile-calendar-container-modal"
        role="dialog"
        show={isModalOpen}
        dropdownModalOpenState={isModalOpen}
        setDropdownModalOpenState={setIsModalOpen}
        mobileTakeOver={true}
        mobileHeaderEnabled={true}
        mobileModalHeading={dates.mobileHeading}
        dropdownModalOnCLoseFunc={handleCloseModal}
        customLastElementRef={buttonRef}
      >
        <div className="modal-content-wrapper">
          <div className="modal-body">
            <MobileCalendar
              dates={dates}
              ctas={{ reset, resetAriaLabel }}
              selectedStartDate={
                dateValues.startDate ? getFormattedDateObject(dateValues.startDate, 'dateWithDayMonthAndYear') : null
              }
              selectedEndDate={
                dateValues.endDate ? getFormattedDateObject(dateValues.endDate, 'dateWithDayMonthAndYear') : null
              }
              isDateRangeFixed={isDateRangeFixed}
              rangeStartDate={rangeStartDate}
              rangeEndDate={rangeEndDate}
              setDefaultDates={setDefaultDates}
              showErrorMessage={showErrorMessage || !!dateError}
              errorMessage={dateError || errorMessage}
              isPastDateSelectionDisabled={isPastDateSelectionDisabled}
              enableFromPastDate={enableFromPastDate}
              calendarTab={calendarTab}
              onCalendarTabChange={handleCalendarTabChange}
              onChange={handleCalendarChange}
              hideDatesBadge={hideDatesBadge}
            />
          </div>
          <div className="modal-common-footer">
            <Button
              buttonCopy={ctas.apply}
              className="m-button-m m-button-secondary apply-btn"
              callback={handleApply}
              ref={buttonRef}
              ariaLabel={ctas.applyAriaLabel}
            />
          </div>
        </div>
      </DropDownModal>
    </StyledMobileCalendarContainer>
  );
};
